import React, { useState } from "react";

import AddAndDeleteButton from "../bundel/AddAndDeleteButton";
import { ArrowDownIcon } from "@heroicons/react/outline";
import { FormBlock, PageContainer, TagInput, TextField } from "../ui";
import Collapsible from "react-collapsible";
import EasyTrans from "../bundel/EasyTrans";
import HelpIcon from "./HelpIcon";
import ISO6391 from "iso-639-1"; //https://www.npmjs.com/package/iso-639-1
import WorkCreatorForm from "./WorkCreatorForm";
import WorktypButton from "./WorktypButton";
import { useTranslation } from "react-i18next";

const WORK_TYPES = [
  "Document",
  "Picture",
  "Audio",
  "Video",
  "Interactive",
  "Software",
  "Hardware",
  "Data",
];

const labelRow = "flex items-center pr-2";
const labelText = "block text-sm font-medium text-gray-700";

const RegisterWorkInformation = (props) => {
  const { t } = useTranslation();
  const [tags, setTags] = useState([]);

  const handleTags = (next) => {
    setTags(next);
    props.onSetTags(next);
  };

  function onAddAuthor() {
    props.onSetContributorList(
      props.contributorList.concat({
        firstName: "",
        lastName: "",
        email: "",
        pseudonym: "",
        url: "",
        percentage: 0,
      }),
    );
  }

  function onDeleteAuthor() {
    props.contributorList.pop();
    props.onSetContributorList([...props.contributorList]);
  }

  const total = props.contributorList.reduce(
    (s, c) => s + (Number(c.percentage) || 0),
    0,
  );

  return (
    <PageContainer>
      <div className="space-y-8">
        {/* Describe the work */}
        <FormBlock
          title={<EasyTrans>RegisterWork.Element.DescriptionWork</EasyTrans>}
        >
          <div className="space-y-6">
            <div>
              <div className={labelRow}>
                <label htmlFor="worktype" className={labelText}>
                  <EasyTrans>RegisterWork.Element.CreationType</EasyTrans>
                  <span className="text-red-600">*</span>
                </label>
                <HelpIcon
                  Title="RegisterWork.Element.CreationType"
                  Message="Auswählen, ob es sich bei dem Werk um ein Dokument, Audio, Bild, Video, Software, Interaktiv, Daten oder Hardware handelt."
                  TransKey="RegisterWork.Element.HelpCreationType"
                />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {WORK_TYPES.map((workType) => (
                  <WorktypButton
                    key={workType}
                    type={workType}
                    helpMessage={
                      <EasyTrans>
                        {`RegisterWork.Element.HelpWorkType${workType}`}
                      </EasyTrans>
                    }
                    onSetType={props.onSetType}
                    typeError={props.typeError}
                    setTypeError={props.setTypeError}
                  />
                ))}
              </div>
              {props.typeError && (
                <span className="mt-2 block text-sm text-red-700">
                  <EasyTrans>RegisterWork.Element.ChooseType</EasyTrans>
                </span>
              )}
            </div>

            <TextField
              id="title"
              name="title"
              type="text"
              required
              label={<EasyTrans>RegisterWork.Element.Title</EasyTrans>}
              labelAddon={
                <HelpIcon
                  Title="RegisterWork.Element.Title"
                  Message="Titel für das Werk vergeben."
                  TransKey="RegisterWork.Element.HelpTitle"
                />
              }
              onChange={(event) => props.onSetTitle(event.target.value)}
              onFocus={() => props.setTitleError(false)}
              error={
                props.titleError ? (
                  <EasyTrans>RegisterWork.Element.ChooseTitle</EasyTrans>
                ) : undefined
              }
            />

            <TextField
              as="textarea"
              id="description"
              name="description"
              rows={3}
              required
              defaultValue={""}
              placeholder={t("RegisterWork.Element.Description")}
              label={<EasyTrans>RegisterWork.Element.Description</EasyTrans>}
              labelAddon={
                <HelpIcon
                  Title="RegisterWork.Element.Description"
                  Message="Eine Beschreibung zum Werk hinzufügen."
                  TransKey="RegisterWork.Element.HelpDescription"
                />
              }
              help={<EasyTrans>RegisterWork.Element.SubDescription</EasyTrans>}
              onChange={(event) => props.onSetDescription(event.target.value)}
              onFocus={() => props.setDescriptionError(false)}
              error={
                props.descriptionError ? (
                  <EasyTrans>RegisterWork.Element.ChooseDescription</EasyTrans>
                ) : undefined
              }
            />

            <div>
              <div className={labelRow}>
                <label htmlFor="tags" className={labelText}>
                  <EasyTrans>RegisterWork.Element.Tags</EasyTrans>
                </label>
                <HelpIcon
                  Title="RegisterWork.Element.Tags"
                  Message="Dem Werk Schlagworte zuordnen, damit danach gefiltert werden kann. Die Tags können auch im Nachhinein hinzugefügt werden."
                  TransKey="RegisterWork.Element.HelpTags"
                />
              </div>
              <TagInput
                value={tags}
                onChange={handleTags}
                placeholder={t("RegisterWork.Element.AddTag")}
              />
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <div className={labelRow}>
                  <label htmlFor="select-language" className={labelText}>
                    <EasyTrans>Language</EasyTrans>
                  </label>
                  <HelpIcon
                    Title="Language"
                    Message="Die Sprache in welcher das Werk verfügbar ist"
                    TransKey="RegisterWork.Element.HelpLanguage"
                  />
                </div>
                <select
                  id="select-language"
                  name="select-language"
                  defaultValue={""}
                  onChange={(event) => props.onSetLanguage(event.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-fairblue-500 focus:outline-none focus:ring-2 focus:ring-fairblue-500 sm:text-sm"
                >
                  <option value="">
                    <EasyTrans>RegisterWork.Element.NoLang</EasyTrans>
                  </option>
                  {ISO6391.getAllNames().map((languagename, index) => (
                    <option key={index} value={ISO6391.getCode(languagename)}>
                      {languagename}
                    </option>
                  ))}
                </select>
              </div>

              <TextField
                id="creationdate"
                name="creationdate"
                type="date"
                label={<EasyTrans>RegisterWork.Element.CreationDate</EasyTrans>}
                labelAddon={
                  <HelpIcon
                    Title="RegisterWork.Element.CreationDate"
                    Message="Wann wurde das Werk fertiggestellt?"
                    TransKey="RegisterWork.Element.HelpCreationDate"
                  />
                }
                onChange={(event) =>
                  props.onSetCreationDate(event.target.value)
                }
              />
            </div>
          </div>
        </FormBlock>

        {/* Contributors */}
        <FormBlock
          title={<EasyTrans>RegisterWork.Element.Contributor</EasyTrans>}
        >
          <div className={labelRow}>
            <label htmlFor="creator" className={labelText}>
              <EasyTrans>RegisterWork.Element.AuthorTitle</EasyTrans>
              <span className="text-red-600">*</span>
            </label>
            <HelpIcon
              Title="RegisterWork.Element.AuthorTitle"
              Message="In der ersten Zeile wird automatisch der eingeloggte Nutzer:in als Urheber:in befüllt. Falls dies nicht dem/der Urheber:in entspricht, muss die erste Zeile geändert werden. Über das Plusicon (+) können weitere Urheber:innen hinzugefügt werden."
              TransKey="RegisterWork.Element.HelpAuthorTitle"
            />
          </div>

          <div className="mt-3 divide-y divide-gray-200">
            {props.contributorList.map((value, index) => (
              <div key={index} className="py-5 first:pt-0">
                <WorkCreatorForm
                  index={index}
                  currentContributor={value}
                  contributorList={props.contributorList}
                  creatorError={props.creatorError}
                  emailError={props.emailError}
                  setEmailError={props.setEmailError}
                  firstnameError={props.firstnameError}
                  setFirstnameError={props.setFirstnameError}
                  lastnameError={props.lastnameError}
                  setLastnameError={props.setLastnameError}
                  percentageError={props.percentageError}
                  setPercentageError={props.setPercentageError}
                  pseudonymError={props.pseudonymError}
                  setPseudonymError={props.setPseudonymError}
                  onPercentageChange={(val) =>
                    props.onSetContributorList(
                      props.contributorList.map((c, i) =>
                        i === index ? { ...c, percentage: val } : c,
                      ),
                    )
                  }
                  noHelpNeeded={false}
                  edit={false}
                />
              </div>
            ))}
          </div>

          {props.contributorList.length > 1 && (
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className={total > 100 ? "font-medium text-red-600" : "text-gray-500"}>
                <EasyTrans>RegisterWork.WorkCreator.TotalShare</EasyTrans>: {total}
                %
              </span>
              {total > 100 && (
                <span className="font-medium text-red-600">
                  <EasyTrans>RegisterWork.WorkCreator.ShareExceeds</EasyTrans>
                </span>
              )}
            </div>
          )}

          <div className="mt-3">
            <AddAndDeleteButton
              deleteAllowed={props.contributorList.length >= 2}
              onAdd={onAddAuthor}
              onDelete={onDeleteAuthor}
              nameAdd={<EasyTrans>MyWorks.WorkListElement.AddAuthor</EasyTrans>}
              nameDelete={
                <EasyTrans>MyWorks.WorkListElement.DeleteAuthor</EasyTrans>
              }
            />
          </div>

          {props.creatorError && (
            <span className="mt-2 block text-sm text-red-700">
              <EasyTrans>RegisterWork.Element.AtLeastOneAuthor</EasyTrans>
            </span>
          )}
        </FormBlock>

        {/* Additional fields (collapsible) */}
        <FormBlock>
          <Collapsible
            trigger={
              <div className="flex cursor-pointer items-center justify-between">
                <h3 className="text-base font-semibold tracking-tight text-fairblue-700">
                  <EasyTrans>RegisterWork.Element.AdditionalFields</EasyTrans>
                </h3>
                <ArrowDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
            }
          >
            <div className="mt-5 grid gap-6 sm:grid-cols-2">
              <TextField
                id="publisher"
                name="publisher"
                type="text"
                label={<EasyTrans>RegisterWork.Element.Publisher</EasyTrans>}
                labelAddon={
                  <HelpIcon
                    Title="RegisterWork.Element.Publisher"
                    Message="Zusätzlicher Herausgeber beispielsweise Buchverlag oder StreamingplattRegisterWork."
                    TransKey="RegisterWork.Element.HelpPublisher"
                  />
                }
                onChange={(event) => props.onSetPublisher(event.target.value)}
              />
              <TextField
                id="identifier"
                name="identifier"
                type="text"
                label={<EasyTrans>RegisterWork.Element.Identifier</EasyTrans>}
                labelAddon={
                  <HelpIcon
                    Title="RegisterWork.Element.Identifier"
                    Message="Kennzeichen mit dem das Werk identifiziert werden kann, beispielsweise ISBN (International Standard Book Number) oder interne Kennzeichen."
                    TransKey="RegisterWork.Element.HelpIdentifikator"
                  />
                }
                onChange={(event) => props.onSetIdentifier(event.target.value)}
              />
            </div>
          </Collapsible>
        </FormBlock>
      </div>
    </PageContainer>
  );
};

export default RegisterWorkInformation;
